import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { FinancialAccount } from '@/types/financial';
import { useToast } from '@/hooks/use-toast';
import { useMultiTenant } from '@/hooks/useMultiTenant';

export const useFinancialAccounts = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { companyId } = useMultiTenant();

  const query = useQuery({
    queryKey: ['financial-accounts', companyId],
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      if (!companyId) throw new Error('Company ID não disponível');
      
      // Lista completa (inclui inativas) — lookups de exibição precisam resolver
      // ids históricos de parcelas; consumidores selecionáveis recebem só ativas.
      const { data, error } = await supabase
        .from('financial_accounts')
        .select('*')
        .eq('company_id', companyId)
        .order('account_name');

      if (error) throw error;
      return (data || []) as FinancialAccount[];
    },
    enabled: !!companyId,
  });

  const createMutation = useMutation({
    mutationFn: async (account: Partial<FinancialAccount>) => {
      if (!companyId) throw new Error('Company ID não disponível');
      
      const { data, error } = await supabase
        .from('financial_accounts')
        .insert([{
          ...account,
          company_id: companyId,
        } as any])
        .select()
        .single();

      if (error) throw error;
      return data as FinancialAccount;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['financial-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      toast({ title: 'Conta financeira criada com sucesso' });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao criar conta financeira',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<FinancialAccount> & { id: string }) => {
      const { data, error } = await supabase
        .from('financial_accounts')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['financial-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      toast({ title: 'Conta financeira atualizada com sucesso' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('financial_accounts')
        .update({ active: false })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-accounts'] });
      toast({ title: 'Conta desativada com sucesso' });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao desativar conta',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const allAccounts = query.data || [];
  // Semântica preservada: consumidores (options, totais) enxergam só ativas;
  // allAccounts existe para lookup de exibição de ids históricos.
  const accounts = allAccounts.filter((acc) => acc.active);

  // Computed values
  const totalBalance = accounts.reduce((sum, acc) => sum + acc.current_balance, 0);
  const totalBankBalance = accounts
    .filter(acc => ['bank', 'checking', 'savings', 'investment'].includes(acc.account_type))
    .reduce((sum, acc) => sum + acc.current_balance, 0);
  const totalCashBalance = accounts
    .filter(acc => acc.account_type === 'cash')
    .reduce((sum, acc) => sum + acc.current_balance, 0);

  return {
    accounts,
    allAccounts,
    isLoading: query.isLoading,
    createAccount: createMutation.mutate,
    updateAccount: updateMutation.mutate,
    deleteAccount: deleteMutation.mutate,
    totalBalance,
    totalBankBalance,
    totalCashBalance,
  };
};
